import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import "./CreateListModal.css"; // Reusing the same CSS file

function EditListModal({
  isOpen,
  onClose,
  onListUpdated,
  availableLeads,
  listToEdit,
}) {
  const { getVerifiedJWT } = useAuth();
  const [listName, setListName] = useState("");
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [isLoadingExistingLeads, setIsLoadingExistingLeads] = useState(false);

  // Fetch existing leads for this list
  const fetchExistingLeads = async listId => {
    if (!listId) return;

    setIsLoadingExistingLeads(true);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(
        getApiEndpoint(`/v1/recruiting/lists/${listId}/leads`),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        const leads = result.data || [];

        setSelectedLeads(leads.map(lead => lead.id));
      }
    } catch (error) {
    } finally {
      setIsLoadingExistingLeads(false);
    }
  };

  useEffect(() => {
    if (isOpen && listToEdit) {
      // Pre-populate the form with existing list data
      setListName(listToEdit.name || "");
      setSearchTerm("");
      setError("");

      // Fetch the existing leads for this list
      fetchExistingLeads(listToEdit.id);
    } else if (!isOpen) {
      // Reset form when modal closes
      setListName("");
      setSelectedLeads([]);
      setSearchTerm("");
      setError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, listToEdit]);

  const handleToggleLead = leadId => {
    setSelectedLeads(prev =>
      prev.includes(leadId)
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    const filteredLeadIds = filteredLeads.map(lead => lead.id);
    if (selectedLeads.length === filteredLeadIds.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(filteredLeadIds);
    }
  };

  const filteredLeads = availableLeads.filter(lead => {
    const fullName = `${lead.first_name} ${lead.last_name}`.toLowerCase();
    const email = (lead.email || "").toLowerCase();
    const search = searchTerm.toLowerCase();
    return fullName.includes(search) || email.includes(search);
  });

  const handleSubmit = async e => {
    e.preventDefault();
    setError("");

    if (!listName.trim()) {
      setError("Please enter a list name");
      return;
    }

    if (!listToEdit?.id) {
      setError("List ID is required");
      return;
    }

    setIsSubmitting(true);

    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setError("Authentication required");
        setIsSubmitting(false);
        return;
      }

      const response = await fetch(getApiEndpoint("/v1/recruiting/lists"), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          list_id: listToEdit.id,
          name: listName.trim(),
          lead_ids:
            selectedLeads.length > 0
              ? selectedLeads.map(id => parseInt(id))
              : [],
        }),
      });

      if (response.ok) {
        const result = await response.json();
        onListUpdated(result.data);
        onClose();
      } else {
        const result = await response.json();
        setError(result.message || "Failed to update list");
      }
    } catch (err) {
      setError("An error occurred while updating the list");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-container create-list-modal"
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 className="modal-title">
            <span className="material-icons">edit</span>
            Edit List
          </h2>
          <button className="modal-close-btn" onClick={onClose}>
            <span className="material-icons">close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && (
              <div className="error-message">
                <span className="material-icons">error</span>
                {error}
              </div>
            )}

            <div className="form-group">
              <label htmlFor="listName" className="form-label required">
                List Name
              </label>
              <input
                type="text"
                id="listName"
                className="form-input"
                placeholder="e.g., High Priority Recruits"
                value={listName}
                onChange={e => setListName(e.target.value)}
                maxLength={100}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Manage Recruits</label>
              <p className="form-helper-text">
                Add or remove recruits from this list
              </p>

              {isLoadingExistingLeads ? (
                <div style={{ textAlign: "center", padding: "2rem" }}>
                  <div className="spinner"></div>
                  <p>Loading existing recruits...</p>
                </div>
              ) : (
                <div className="recruits-selection-container">
                  <div className="recruits-search">
                    <span className="material-icons search-icon">search</span>
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search recruits..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                    />
                  </div>

                  <div className="recruits-list-header">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={
                          selectedLeads.length === filteredLeads.length &&
                          filteredLeads.length > 0
                        }
                        onChange={handleSelectAll}
                      />
                      <span>
                        {selectedLeads.length > 0
                          ? `${selectedLeads.length} selected`
                          : "Select all"}
                      </span>
                    </label>
                  </div>

                  <div className="recruits-list">
                    {filteredLeads.length === 0 ? (
                      <div className="empty-recruits-state">
                        <span className="material-icons">people_outline</span>
                        <p>
                          {searchTerm
                            ? "No recruits found"
                            : "No recruits available"}
                        </p>
                      </div>
                    ) : (
                      filteredLeads.map(lead => (
                        <label key={lead.id} className="recruit-item">
                          <input
                            type="checkbox"
                            checked={selectedLeads.includes(lead.id)}
                            onChange={() => handleToggleLead(lead.id)}
                          />
                          <span className="recruit-name">
                            {lead.first_name} {lead.last_name}
                          </span>
                          <span className="recruit-status">{lead.status}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting || !listName.trim()}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  Updating...
                </>
              ) : (
                <>
                  <span className="material-icons">save</span>
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditListModal;
